/**
 * Converts between DSA/Girder annotation documents (large_image schema with `elements[]`)
 * and osd-paperjs-annotation GeoJSON FeatureCollections (toolkit `toGeoJSON()` / `loadGeoJSON` format).
 *
 * **Usage:** Load path — pass a DSA annotation (e.g. from the annotation API) to
 * `dsaToAnnotationToolkit`; pass the returned array to the toolkit's `addFeatureCollections`.
 * Save path — pass the array from `toolkit.toGeoJSON()` to `annotationToolkitToDSA`; use the
 * returned elements in the annotation document for PUT/POST.
 *
 * In this module, "dsaElement" means one annotation in DSA format (one entry in `elements[]`);
 * "toolkitFeature" means one annotation in toolkit/GeoJSON format (one Feature in `features[]`).
 *
 * For full element and feature shapes, validation behavior, and supported types, see
 * `docs/annotation-adapter-contract.md`.
 *
 * This module does not mutate its inputs; all returned objects and arrays are new.
 *
 * @module utils/annotationAdapter
 */

import { getFeatureGirderProjectsUserdata } from './annotationUserdata';

/** DSA `elements[].user` key holding a clone of toolkit `feature.properties.userdata` (minus `dsa`). */
export const DSA_USER_KEY_PAPER_USERDATA = 'paperUserdata';

/** Set to true to log adapter load/save details to the console; default false for production. */
const ADAPTER_DEBUG = false;
function debugLog(...args) {
    if (ADAPTER_DEBUG) console.log(...args);
}

/**
 * Deep-clone plain JSON userdata and drop `dsa` so we do not persist stale element ids (Option A).
 * @param {unknown} userdata
 * @returns {Record<string, unknown>}
 */
function cloneUserdataForPaperUserdataField(userdata) {
    if (!userdata || typeof userdata !== 'object' || Array.isArray(userdata)) {
        return {};
    }
    try {
        const o = JSON.parse(JSON.stringify(userdata));
        if (o && typeof o === 'object' && !Array.isArray(o)) {
            delete o.dsa;
        }
        return o;
    } catch {
        return {};
    }
}

/**
 * Build `feature.properties.userdata` on load: merge `user.paperUserdata` with canonical `dsa.elementId`.
 * @param {Object} dsaElement
 * @returns {Record<string, unknown>}
 */
function buildFeatureUserdataFromDsaElement(dsaElement) {
    const raw = dsaElement.user?.[DSA_USER_KEY_PAPER_USERDATA];
    const base =
        raw && typeof raw === 'object' && !Array.isArray(raw)
            ? JSON.parse(JSON.stringify(raw))
            : {};
    delete base.dsa;
    return {
        ...base,
        dsa: { elementId: dsaElement.id },
    };
}

/**
 * Attach `user.paperUserdata` to one or more DSA elements returned from geometry handlers (sibling to Multi*, subtype, etc.).
 * @param {Object|Object[]} result
 * @param {Object} properties - toolkit feature properties (source of `userdata`)
 * @returns {Object|Object[]}
 */
function attachPaperUserdataToDsaResult(result, properties) {
    const payload = cloneUserdataForPaperUserdataField(properties?.userdata);
    const attachOne = (el) => {
        if (!el || typeof el !== 'object') return el;
        const u = el.user && typeof el.user === 'object' ? el.user : {};
        el.user = {
            ...u,
            [DSA_USER_KEY_PAPER_USERDATA]: JSON.parse(JSON.stringify(payload)),
        };
        return el;
    };
    if (Array.isArray(result)) {
        result.forEach(attachOne);
        return result;
    }
    return attachOne(result);
}

/** Values for `feature.geometry.type` (GeoJSON / toolkit). Supported: Point, LineString, Polygon, MultiPoint, MultiLineString, MultiPolygon, GeometryCollection. */
const FEATURE_GEOM_MULTI_POLYGON = 'MultiPolygon';
const FEATURE_GEOM_POLYGON = 'Polygon';
const FEATURE_GEOM_MULTI_LINESTRING = 'MultiLineString';
const FEATURE_GEOM_LINE_STRING = 'LineString';
const FEATURE_GEOM_POINT = 'Point';
const FEATURE_GEOM_GEOMETRY_COLLECTION = 'GeometryCollection';

/** Values for `feature.geometry.properties.subtype`. Supported: Measurement, Arrow, Rectangle, RectangleGrid, Ellipse, PointText, Raster. */
const FEATURE_SUBTYPE_MEASUREMENT = 'Measurement';
const FEATURE_SUBTYPE_ARROW = 'Arrow';
const FEATURE_SUBTYPE_RECTANGLE = 'Rectangle';
const FEATURE_SUBTYPE_RECTANGLE_GRID = 'RectangleGrid';
const FEATURE_SUBTYPE_ELLIPSE = 'Ellipse';
const FEATURE_SUBTYPE_POINT_TEXT = 'PointText';
const FEATURE_SUBTYPE_RASTER = 'Raster';

/** Values for `element.type` in the DSA annotation document. Supported: polyline, arrow, rectangle, rectanglegrid, circle, ellipse, point, griddata. */
const DSA_ELEMENT_TYPE_POLYLINE = 'polyline';
const DSA_ELEMENT_TYPE_ARROW = 'arrow';
const DSA_ELEMENT_TYPE_RECTANGLE = 'rectangle';
const DSA_ELEMENT_TYPE_RECTANGLE_GRID = 'rectanglegrid';
const DSA_ELEMENT_TYPE_CIRCLE = 'circle';
const DSA_ELEMENT_TYPE_ELLIPSE = 'ellipse';
const DSA_ELEMENT_TYPE_POINT = 'point';
const DSA_ELEMENT_TYPE_GRIDDATA = 'griddata';

/** Values for `element.user.subtype` in the DSA document (e.g. pointtext, raster). */
const DSA_USER_SUBTYPE_POINTTEXT = 'pointtext';
const DSA_USER_SUBTYPE_RASTER = 'raster';

/**
 * Converts a DSA annotation document into GeoJSON FeatureCollections for the toolkit's addFeatureCollections().
 * If `annotation.attributes.geojslayer` is a valid FeatureCollection, it is returned as the only collection;
 * otherwise `annotation.elements` is converted and grouped by `element.group`. Returns empty array when
 * input is missing `annotation`, or when elements is non-array/empty (and geojslayer is not used).
 *
 * @param {Object} dsaAnnotation - DSA annotation document; must have `annotation` (with optional
 *   `elements`, `name`, `description`, `attributes.geojslayer`). See docs/annotation-adapter-contract.md.
 * @returns {Object[]} Array of GeoJSON FeatureCollections (one per group). Empty array if input invalid or no elements.
 */
export function dsaToAnnotationToolkit(dsaAnnotation) {
    const dsa = dsaAnnotation;
    if (!dsa?.annotation) return [];

    const geojslayer = dsa.annotation.attributes?.geojslayer;
    // Some annotations may store prebuilt geojslayer FeatureCollections; only accept valid shape.
    if (geojslayer && geojslayer.type === 'FeatureCollection' && Array.isArray(geojslayer.features)) {
        return [geojslayer];
    }

    const dsaElements = dsa.annotation.elements || [];
    if (!Array.isArray(dsaElements) || dsaElements.length === 0) return [];

    if (ADAPTER_DEBUG) {
        dsaElements.forEach((dsaEl) => {
            debugLog('[BDSA opacity LOAD] DSA element fillColor:', { type: dsaEl.type, fillColor: dsaEl.fillColor });
        });
    }

    const groups = dsaElements.reduce((acc, dsaElement) => {
        const label = 'group' in dsaElement ? dsaElement.group : dsa.annotation.name || 'Default';
        if (!acc[label]) {
            acc[label] = [];
            acc[label].group = dsaElement.group;
        }
        acc[label].push(dsaElement);
        return acc;
    }, {});

    const featureCollections = [];
    Object.keys(groups).forEach((label) => {
        const groupDsaElements = groups[label];
        const description = dsa.annotation.description;
        featureCollections.push(
            dsaElementsToFeatureCollection(dsa._id, label, groupDsaElements, description, groupDsaElements.group)
        );
    });
    return featureCollections;
}

/**
 * Converts an array of GeoJSON FeatureCollections (e.g. from toolkit.toGeoJSON()) into DSA annotation elements.
 * Each FeatureCollection's `label` becomes the `group` on its elements. Returns empty array if `toolkitOutput` is
 * not an array. Skips non-object FC items; treats missing or non-array `features` as empty.
 *
 * @param {Object[]} toolkitOutput - Array of FeatureCollections (e.g. from toolkit.toGeoJSON()); each item should have `features`.
 * @returns {Object[]} Flat array of DSA elements for the annotation document (e.g. POST/PUT body).
 * @throws {Error} If a feature is invalid (e.g. missing geometry or type). Message prefix: "Annotation adapter: ".
 */
export function annotationToolkitToDSA(toolkitOutput) {
    if (!Array.isArray(toolkitOutput)) return [];
    const dsaElements = toolkitOutput.flatMap((geojson) => {
        if (!geojson || typeof geojson !== 'object') return [];
        const toolkitFeatures = Array.isArray(geojson.features) ? geojson.features : [];
        return toolkitFeatures.flatMap((toolkitFeature) => {
            if (getFeatureGirderProjectsUserdata(toolkitFeature).readOnlyRoi) {
                return [];
            }
            const el = toolkitFeatureToDsaElement(toolkitFeature);
            const arr = Array.isArray(el) ? el : [el];
            return arr.map((dsaElement) => {
                dsaElement.group = '' + (geojson.label ?? 'Default');
                debugLog('[BDSA opacity SAVE] DSA element fillColor:', { type: dsaElement.type, fillColor: dsaElement.fillColor });
                return dsaElement;
            });
        });
    });
    return dsaElements;
}

// --- private helpers (ported from submodule demo) ---

/**
 * Converts one DSA element to a GeoJSON Feature for the toolkit. Does not mutate the input.
 *
 * @param {Object} dsaElement - One DSA element (see docs/annotation-adapter-contract.md for required fields per type).
 * @returns {{ type: 'Feature', geometry: Object, properties: Object }} GeoJSON Feature for the toolkit.
 */
function dsaElementToToolkitFeature(dsaElement) {
    // DSA stores zero lineWidth as a very small float; normalize for feature building without mutating input.
    const effectiveLineWidth =
        dsaElement.lineWidth == null ? 1 : dsaElement.lineWidth < 0.001 ? 0 : dsaElement.lineWidth;
    const elementForGeometry = { ...dsaElement, lineWidth: effectiveLineWidth };

    function mapElementToGeometryType(e) {
        const g = {
            type: null,
            coordinates: [],
            properties: { label: e.label?.value },
        };

        const key =
            e.type === DSA_ELEMENT_TYPE_POLYLINE
                ? e.closed === true
                    ? 'polyline:closed'
                    : e.closed === false
                        ? 'polyline:open'
                        : 'polyline'
                : e.type === DSA_ELEMENT_TYPE_POINT
                    ? e.user?.subtype === DSA_USER_SUBTYPE_POINTTEXT || e.user?.text
                        ? 'point:pointtext'
                        : e.user?.subtype === DSA_USER_SUBTYPE_RASTER
                            ? 'point:raster'
                            : 'point'
                    : e.type;

        const handlers = {
            'polyline:closed': () => {
                g.type = FEATURE_GEOM_MULTI_POLYGON;
                g.coordinates = [[e.points].concat(e.holes || [])];
                return g;
            },
            'polyline:open': () => {
                g.type = FEATURE_GEOM_MULTI_LINESTRING;
                // One segment = the points array; [e.points] not [[e.points]] (extra bracket made segment length 1)
                g.coordinates = [e.points];
                g.properties.strokeWidths = [e.lineWidth];
                // Measurement: restore subtype and ruler data for RulerMeasurement constructor
                if (e.user?.subtype === FEATURE_SUBTYPE_MEASUREMENT && e.user.ruler) {
                    g.properties.subtype = FEATURE_SUBTYPE_MEASUREMENT;
                    Object.assign(g.properties, e.user.ruler);
                }
                return g;
            },
            [DSA_ELEMENT_TYPE_ARROW]: () => {
                g.type = FEATURE_GEOM_LINE_STRING;
                g.coordinates = [e.points];
                g.properties.subtype = FEATURE_SUBTYPE_ARROW;
                return g;
            },
            [DSA_ELEMENT_TYPE_RECTANGLE]: () => {
                g.type = FEATURE_GEOM_POINT;
                g.properties.subtype = FEATURE_SUBTYPE_RECTANGLE;
                g.coordinates = e.center.slice(0, 2);
                g.properties.width = e.width;
                g.properties.height = e.height;
                g.properties.angle = (e.rotation * 180) / Math.PI;
                return g;
            },
            [DSA_ELEMENT_TYPE_RECTANGLE_GRID]: () => {
                g.type = FEATURE_GEOM_POINT;
                g.properties.subtype = FEATURE_SUBTYPE_RECTANGLE_GRID;
                g.coordinates = e.origin.slice(0, 2);
                g.properties.gridWidth = e.gridWidth;
                return g;
            },
            [DSA_ELEMENT_TYPE_CIRCLE]: () => {
                g.type = FEATURE_GEOM_POINT;
                g.properties.subtype = FEATURE_SUBTYPE_ELLIPSE;
                g.coordinates = e.center.slice(0, 2);
                g.properties.majorRadius = g.properties.minorRadius = e.radius;
                g.properties.angle = 0;
                return g;
            },
            [DSA_ELEMENT_TYPE_ELLIPSE]: () => {
                g.type = FEATURE_GEOM_POINT;
                g.properties.subtype = FEATURE_SUBTYPE_ELLIPSE;
                g.coordinates = e.center.slice(0, 2);
                g.properties.majorRadius = e.width / 2;
                g.properties.minorRadius = e.height / 2;
                g.properties.angle = (e.rotation * 180) / Math.PI;
                return g;
            },
            'point:pointtext': () => {
                g.type = FEATURE_GEOM_POINT;
                g.properties.subtype = FEATURE_SUBTYPE_POINT_TEXT;
                g.coordinates = e.center.slice(0, 2);
                g.properties.content = e.user.text;
                return g;
            },
            'point:raster': () => {
                // Raster: pass-through blob (demo pattern). Support string if API returned serialized JSON; ensure geometries array for Raster constructor.
                const raw = e.user.json;
                let geom = null;
                if (typeof raw === 'string') {
                    try {
                        geom = JSON.parse(raw);
                    } catch (_) {
                        throw new Error('Raster element user.json is invalid JSON');
                    }
                } else {
                    geom = raw;
                }
                if (!geom || typeof geom !== 'object') {
                    throw new Error('Raster element has no valid user.json');
                }
                if (Array.isArray(geom.geometries)) {
                    return geom;
                }
                return { ...geom, geometries: geom.geometries || [] };
            },
            point: () => {
                g.type = FEATURE_GEOM_POINT;
                g.coordinates = e.center.slice(0, 2);
                return g;
            },
        };

        const handler = handlers[key];
        const geom = handler ? handler() : null;
        if (!geom?.type) {
            throw new Error('No GeoJSON Geometry defined for annotation type: ' + (e.type || 'unknown'));
        }
        return geom;
    }

    const geometry = mapElementToGeometryType(elementForGeometry);
    const strokeWidth = typeof dsaElement.lineWidth === 'undefined' ? 1 : effectiveLineWidth;

    // Raster: API does not return top-level fillColor/lineColor; prefer dsaElement.user.* and use explicit
    // fillOpacity/strokeOpacity properties, letting the toolkit apply them on import.
    const isRaster =
        dsaElement.type === DSA_ELEMENT_TYPE_POINT && dsaElement.user?.subtype === DSA_USER_SUBTYPE_RASTER;
    const defaultFill = isRaster ? 'rgba(255, 255, 255, 1)' : 'rgba(255, 255, 255, 0)';
    const defaultStroke = isRaster ? 'rgba(0, 0, 0, 1)' : 'rgba(0, 0, 0, 0)';
    const baseFillColor =
        (isRaster ? (dsaElement.user?.fillColor ?? dsaElement.fillColor) : dsaElement.fillColor) ?? defaultFill;
    const baseStrokeColor = (isRaster ? dsaElement.user?.lineColor ?? dsaElement.lineColor : dsaElement.lineColor) ?? defaultStroke;

    // Raster: API may return fillOpacity/strokeOpacity as strings; parse to numbers for toolkit import.
    const rasterFillOpacity =
        isRaster && dsaElement.user?.fillOpacity != null ? Number(dsaElement.user.fillOpacity) : null;
    const rasterStrokeOpacity =
        isRaster && dsaElement.user?.strokeOpacity != null ? Number(dsaElement.user.strokeOpacity) : null;

    // Pass colors as stored; rely on upstream toolkit to honor explicit fillOpacity/strokeOpacity when present.
    const properties = {
        userdata: buildFeatureUserdataFromDsaElement(dsaElement),
        fillColor: baseFillColor,
        strokeColor: baseStrokeColor,
        strokeWidth,
        rescale: { strokeWidth },
        label: dsaElement.label?.value,
    };
    if (isRaster) {
        if (rasterFillOpacity != null) properties.fillOpacity = rasterFillOpacity;
        if (rasterStrokeOpacity != null) properties.strokeOpacity = rasterStrokeOpacity;
    }
    if (dsaElement.type === DSA_ELEMENT_TYPE_POINT) {
        if (dsaElement.user?.subtype === DSA_USER_SUBTYPE_POINTTEXT) {
            debugLog(
                '[BDSA adapter LOAD] PointText feature we\'re sending to toolkit:',
                JSON.stringify({ geometry, properties }, null, 2)
            );
        } else if (dsaElement.user?.subtype === DSA_USER_SUBTYPE_RASTER) {
            debugLog('[BDSA adapter LOAD] Raster feature geometry:', {
                type: geometry.type,
                subtype: geometry.properties?.subtype,
                hasRaster: !!geometry.properties?.raster,
                geometriesLength: geometry.geometries?.length,
            });
        } else {
            debugLog('[BDSA adapter LOAD] Point from element:', {
                elementLineWidth: dsaElement.lineWidth,
                featureStrokeWidth: strokeWidth,
            });
        }
    }
    if (dsaElement.type === DSA_ELEMENT_TYPE_POLYLINE && dsaElement.closed === false) {
        const pts = dsaElement.points;
        const pointsLen = Array.isArray(pts) ? pts.length : 0;
        const firstPt = pointsLen > 0 ? pts[0] : null;
        const lastPt = pointsLen > 1 ? pts[pointsLen - 1] : null;
        debugLog('[BDSA adapter LOAD] Linestring (polyline) from DSA:', {
            type: dsaElement.type,
            closed: dsaElement.closed,
            pointsCount: pointsLen,
            pointsIsArray: Array.isArray(pts),
            firstPoint: firstPt,
            lastPoint: lastPt,
            geometryCoordinatesSegments: geometry.coordinates?.length,
            firstSegmentLength: geometry.coordinates?.[0]?.length,
        });
    }
    return {
        type: 'Feature',
        geometry,
        properties,
    };
}

/**
 * Converts one GeoJSON Feature (e.g. from the toolkit) to one or more DSA elements. MultiPolygon and
 * MultiLineString produce an array of elements (one per segment); all other types produce a single element.
 *
 * @param {Object} toolkitFeature - GeoJSON Feature; must have `geometry` and `geometry.type`. Point must have at least two coordinates.
 * @returns {Object|Object[]} One DSA element, or array of elements for MultiPolygon / MultiLineString.
 * @throws {Error} If feature or geometry is missing, type/coordinates invalid, or type/subtype unsupported. Message prefix: "Annotation adapter: ".
 */
function toolkitFeatureToDsaElement(toolkitFeature) {
    if (!toolkitFeature || typeof toolkitFeature !== 'object') {
        throw new Error('Annotation adapter: feature must be an object');
    }
    const g = toolkitFeature.geometry;
    if (!g || typeof g !== 'object') {
        throw new Error('Annotation adapter: feature must have a geometry object');
    }
    if (!g.type) {
        throw new Error('Annotation adapter: feature.geometry must have a type');
    }
    const p = toolkitFeature.properties && typeof toolkitFeature.properties === 'object' ? toolkitFeature.properties : {};
    // Normalize to CSS string (toolkit may pass paper.Color in-memory; support .toCSS())
    const normalizedFill =
        p.fillColor && typeof p.fillColor.toCSS === 'function' ? p.fillColor.toCSS() : (p.fillColor ?? '');
    const normalizedStroke =
        p.strokeColor && typeof p.strokeColor.toCSS === 'function' ? p.strokeColor.toCSS() : (p.strokeColor ?? '');
    // Omit fillColor/lineColor when missing (demo pattern); undefined → key omitted in JSON
    const dsaElement = {
        type: null,
        label: { value: p.label },
        fillColor: normalizedFill ? getColorString(normalizedFill, p.fillOpacity) : undefined,
        lineColor: normalizedStroke ? getColorString(normalizedStroke, p.strokeOpacity) : undefined,
        lineWidth: Math.max(0.0001, parseFloat(p.rescale?.strokeWidth ?? p.strokeWidth ?? 1)),
        user: {},
    };

    const subtype = g.properties?.subtype ?? '';
    const key = `${g.type}:${subtype}`;

    const handlers = {
        [`${FEATURE_GEOM_MULTI_POLYGON}:`]: () => {
            const multiPolygonID = makeGUID();
            return g.coordinates.map((c) => {
                const newFeature = { properties: p, geometry: { ...g, coordinates: c, type: FEATURE_GEOM_POLYGON } };
                const singlePoly = toolkitFeatureToDsaElement(newFeature);
                singlePoly.user = { ...singlePoly.user, MultiPolygon: multiPolygonID };
                return singlePoly;
            });
        },
        [`${FEATURE_GEOM_POLYGON}:`]: () => {
            dsaElement.type = DSA_ELEMENT_TYPE_POLYLINE;
            dsaElement.closed = true;
            const pointArrays = g.coordinates.map((c) => c.map((pt) => [pt[0], pt[1], 0]));
            dsaElement.points = pointArrays[0];
            dsaElement.holes = pointArrays.slice(1);
            return dsaElement;
        },
        [`${FEATURE_GEOM_MULTI_LINESTRING}:`]: () => {
            const strokeWidths = g.properties?.strokeWidths;
            const multiLineStringID = makeGUID();
            const geomProps = g.properties || {};
            // Measurement: MultiLineString with subtype; build polyline elements directly (like PointText with user data)
            if (geomProps.subtype === FEATURE_SUBTYPE_MEASUREMENT) {
                return g.coordinates.map((c, index) => {
                    const segmentPoints = c.map((pt) => [pt[0], pt[1], 0]);
                    const strokeW = strokeWidths?.[index] ?? p.strokeWidth ?? 1;
                    const segEl = {
                        type: DSA_ELEMENT_TYPE_POLYLINE,
                        closed: false,
                        label: { value: p.label },
                        fillColor: normalizedFill ? getColorString(normalizedFill, p.fillOpacity) : undefined,
                        lineColor: normalizedStroke ? getColorString(normalizedStroke, p.strokeOpacity) : undefined,
                        lineWidth: Math.max(0.0001, parseFloat(strokeW)),
                        user: {
                            MultiLineString: multiLineStringID,
                            subtype: FEATURE_SUBTYPE_MEASUREMENT,
                            ...(index === 0 && {
                                ruler: {
                                    units: geomProps.units,
                                    unitsPerPixel: geomProps.unitsPerPixel,
                                    strokeWidthPixels: geomProps.strokeWidthPixels,
                                    haloExtraPixels: geomProps.haloExtraPixels,
                                    labelFontSize: geomProps.labelFontSize,
                                    lengths: geomProps.lengths,
                                },
                            }),
                        },
                    };
                    segEl.points = segmentPoints;
                    return segEl;
                });
            }
            // Ordinary MultiLineString: recurse per segment
            return g.coordinates.map((c, index) => {
                const newFeature = {
                    properties: { ...p, strokeWidth: strokeWidths?.[index] ?? p.strokeWidth },
                    geometry: { ...g, coordinates: c, type: FEATURE_GEOM_LINE_STRING },
                };
                const singleLineString = toolkitFeatureToDsaElement(newFeature);
                singleLineString.user = { ...singleLineString.user, MultiLineString: multiLineStringID };
                return singleLineString;
            });
        },
        [`${FEATURE_GEOM_LINE_STRING}:`]: () => {
            dsaElement.type = DSA_ELEMENT_TYPE_POLYLINE;
            dsaElement.closed = false;
            dsaElement.points = g.coordinates.map((pt) => [pt[0], pt[1], 0]);
            debugLog('[BDSA adapter SAVE] Linestring element:', {
                geometryType: g.type,
                pointsCount: dsaElement.points.length,
                firstPoint: dsaElement.points[0],
                lastPoint: dsaElement.points[dsaElement.points.length - 1],
            });
            return dsaElement;
        },
        [`${FEATURE_GEOM_LINE_STRING}:${FEATURE_SUBTYPE_ARROW}`]: () => {
            dsaElement.type = DSA_ELEMENT_TYPE_ARROW;
            dsaElement.points = g.coordinates.slice(0, 2).map((pt) => [pt[0], pt[1], 0]);
            return dsaElement;
        },
        [`${FEATURE_GEOM_POINT}:`]: () => {
            if (!Array.isArray(g.coordinates) || g.coordinates.length < 2) {
                throw new Error('Annotation adapter: Point geometry must have at least two coordinates');
            }
            dsaElement.type = DSA_ELEMENT_TYPE_POINT;
            dsaElement.center = [g.coordinates[0], g.coordinates[1], 0];
            return dsaElement;
        },
        [`${FEATURE_GEOM_POINT}:${FEATURE_SUBTYPE_RECTANGLE}`]: () => {
            dsaElement.type = DSA_ELEMENT_TYPE_RECTANGLE;
            dsaElement.center = [g.coordinates[0], g.coordinates[1], 0];
            dsaElement.width = g.properties.width;
            dsaElement.height = g.properties.height;
            dsaElement.rotation = (g.properties.angle * Math.PI) / 180;
            return dsaElement;
        },
        [`${FEATURE_GEOM_POINT}:${FEATURE_SUBTYPE_RECTANGLE_GRID}`]: () => {
            dsaElement.type = DSA_ELEMENT_TYPE_GRIDDATA;
            dsaElement.origin = [g.coordinates[0], g.coordinates[1], 0];
            dsaElement.gridWidth = g.properties.gridWidth;
            return dsaElement;
        },
        [`${FEATURE_GEOM_POINT}:${FEATURE_SUBTYPE_ELLIPSE}`]: () => {
            dsaElement.type = DSA_ELEMENT_TYPE_ELLIPSE;
            dsaElement.center = [g.coordinates[0], g.coordinates[1], 0];
            dsaElement.width = g.properties.majorRadius * 2;
            dsaElement.height = g.properties.minorRadius * 2;
            dsaElement.rotation = (g.properties.angle * Math.PI) / 180;
            if (g.properties.majorRadius === g.properties.minorRadius) {
                dsaElement.type = DSA_ELEMENT_TYPE_CIRCLE;
                dsaElement.radius = dsaElement.width;
                delete dsaElement.width;
                delete dsaElement.height;
                delete dsaElement.rotation;
            }
            return dsaElement;
        },
        [`${FEATURE_GEOM_POINT}:${FEATURE_SUBTYPE_POINT_TEXT}`]: () => {
            dsaElement.type = DSA_ELEMENT_TYPE_POINT;
            dsaElement.center = [g.coordinates[0], g.coordinates[1], 0];
            dsaElement.user = { text: g.properties.content, subtype: DSA_USER_SUBTYPE_POINTTEXT };
            return dsaElement;
        },
        [`${FEATURE_GEOM_GEOMETRY_COLLECTION}:${FEATURE_SUBTYPE_RASTER}`]: () => {
            dsaElement.type = DSA_ELEMENT_TYPE_POINT;
            dsaElement.center = [g.properties.raster.center[0], g.properties.raster.center[1], 0];
            dsaElement.user = {
                subtype: DSA_USER_SUBTYPE_RASTER,
                json: g,
                fillColor: dsaElement.fillColor,
                fillOpacity: p.fillOpacity,
                lineColor: dsaElement.lineColor,
                strokeOpacity: p.strokeOpacity,
            };
            return dsaElement;
        },
    };

    const handler = handlers[key] || handlers[`${g.type}:`];
    if (!handler) {
        throw new Error(`Annotation adapter: unsupported geometry type/subtype: ${key}`);
    }
    const result = handler();
    return attachPaperUserdataToDsaResult(result, p);
}

/**
 * Builds a single FeatureCollection from an array of DSA elements in one group. Merges MultiPolygon,
 * MultiLineString, and MultiPoint segments that share the same user.Multi* ID into one feature each.
 *
 * @param {string} annotationId - Annotation ID; stored in properties.userdata.dsa.annotationId.
 * @param {string} label - Group label (becomes FeatureCollection.label).
 * @param {Object[]} dsaElements - DSA elements in this group.
 * @param {string} [description] - Annotation description; stored in properties.userdata.dsa.
 * @param {string} [groupName] - Group name for DSA metadata.
 * @returns {{ type: 'FeatureCollection', features: Object[], label: string, properties: Object }}
 */
function dsaElementsToFeatureCollection(annotationId, label, dsaElements, description, groupName) {
    const grouped = dsaElements.reduce(
        (acc, dsaElement) => {
            const toolkitFeature = dsaElementToToolkitFeature(dsaElement);
            if (dsaElement.user?.MultiPolygon) {
                const id = dsaElement.user.MultiPolygon;
                if (!acc.multiPolygons[id]) {
                    const idx = acc.featureList.length;
                    acc.multiPolygons[id] = { feature: toolkitFeature, idx };
                    acc.featureList.push(toolkitFeature);
                } else {
                    const existingEntry = acc.multiPolygons[id];
                    const existing = existingEntry.feature;
                    const newCoords = [...existing.geometry.coordinates, toolkitFeature.geometry.coordinates[0]];
                    const merged = {
                        type: 'Feature',
                        geometry: { ...existing.geometry, coordinates: newCoords },
                        properties: { ...existing.properties },
                    };
                    acc.featureList[existingEntry.idx] = merged;
                    acc.multiPolygons[id] = { feature: merged, idx: existingEntry.idx };
                }
            } else if (dsaElement.user?.MultiLineString) {
                const id = dsaElement.user.MultiLineString;
                if (!acc.multiLineStrings[id]) {
                    const idx = acc.featureList.length;
                    acc.multiLineStrings[id] = { feature: toolkitFeature, idx };
                    acc.featureList.push(toolkitFeature);
                } else {
                    const existingEntry = acc.multiLineStrings[id];
                    const existing = existingEntry.feature;
                    const newCoords = [...existing.geometry.coordinates, toolkitFeature.geometry.coordinates[0]];
                    const nextWidth = toolkitFeature.geometry.properties?.strokeWidths?.[0];
                    const existingWidths = existing.geometry.properties?.strokeWidths;
                    const mergedGeomProps =
                        Array.isArray(existingWidths) && nextWidth != null
                            ? { ...existing.geometry.properties, strokeWidths: [...existingWidths, nextWidth] }
                            : existing.geometry.properties;
                    const merged = {
                        type: 'Feature',
                        geometry: { ...existing.geometry, coordinates: newCoords, properties: mergedGeomProps },
                        properties: { ...existing.properties },
                    };
                    acc.featureList[existingEntry.idx] = merged;
                    acc.multiLineStrings[id] = { feature: merged, idx: existingEntry.idx };
                }
            } else if (dsaElement.user?.MultiPoint) {
                const id = dsaElement.user.MultiPoint;
                if (!acc.multiPoints[id]) {
                    const idx = acc.featureList.length;
                    acc.multiPoints[id] = { feature: toolkitFeature, idx };
                    acc.featureList.push(toolkitFeature);
                } else {
                    const existingEntry = acc.multiPoints[id];
                    const existing = existingEntry.feature;
                    const newCoords = [...existing.geometry.coordinates, toolkitFeature.geometry.coordinates[0]];
                    const merged = {
                        type: 'Feature',
                        geometry: { ...existing.geometry, coordinates: newCoords },
                        properties: { ...existing.properties },
                    };
                    acc.featureList[existingEntry.idx] = merged;
                    acc.multiPoints[id] = { feature: merged, idx: existingEntry.idx };
                }
            } else {
                acc.featureList.push(toolkitFeature);
            }
            return acc;
        },
        { featureList: [], multiPolygons: {}, multiLineStrings: {}, multiPoints: {} }
    );

    return {
        type: 'FeatureCollection',
        features: grouped.featureList,
        label,
        properties: {
            userdata: {
                dsa: {
                    annotationId,
                    group: groupName,
                    annotationDescription: description,
                },
            },
        },
    };
}

/** Returns a new UUID-like string used as merge IDs for MultiPolygon / MultiLineString / MultiPoint features. */
function makeGUID() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}

/**
 * Compose a CSS color string with a given opacity.
 * Mirrors the intent of the demo DSA adapter's getColorString, but operates on CSS strings
 * and an explicit opacity value (typically StyleTool-controlled fillOpacity / strokeOpacity).
 *
 * @param {string} color - CSS color string (e.g. '#rrggbb', 'rgb(...)', 'rgba(...)').
 * @param {number} opacity - Per-item opacity in [0,1]. If undefined/null, the color is returned unchanged.
 * @returns {string} CSS rgba() string with the requested opacity when possible; otherwise the original color.
 */
function getColorString(color, opacity) {
    if (!color || opacity == null) {
        return color;
    }

    const a = Math.max(0, Math.min(1, Number(opacity)));
    if (Number.isNaN(a)) {
        return color;
    }

    // Hex colors: #rgb or #rrggbb
    const hexMatch = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(color);
    if (hexMatch) {
        let hex = hexMatch[1];
        if (hex.length === 3) {
            hex = hex.split('').map((c) => c + c).join('');
        }
        const r = parseInt(hex.slice(0, 2), 16);
        const g = parseInt(hex.slice(2, 4), 16);
        const b = parseInt(hex.slice(4, 6), 16);
        return `rgba(${r}, ${g}, ${b}, ${a})`;
    }

    // rgb() or rgba()
    const rgbMatch = /^rgba?\s*\(\s*([0-9.]+)\s*,\s*([0-9.]+)\s*,\s*([0-9.]+)(?:\s*,\s*([0-9.]+))?\s*\)$/i.exec(color);
    if (rgbMatch) {
        const r = Number(rgbMatch[1]);
        const g = Number(rgbMatch[2]);
        const b = Number(rgbMatch[3]);
        return `rgba(${r}, ${g}, ${b}, ${a})`;
    }

    // Fallback: unknown format, return as-is.
    return color;
}
